import React, { memo, useMemo } from "react";
import cx from "classnames";
import s from "./Styles.module.scss";

export const useIndentComponent = (
  { parent, rowIndex, level, id, data },
  options,
  isVisualSibling,
  subType = "",
) =>
  useMemo(() => {
    const indicators = getSpacers({
      parent,
      rowIndex,
      level,
      isVisualSibling,
      data,
      subType,
    });

    const IndentRenderer = options.isIndentCompact
      ? CompactIndentComponent
      : IndentComponent;

    return (
      <div className="d-flex flex-row h-100 position-absolute start-0">
        {indicators.map((indicator, index) => (
          <IndentRenderer
            indicator={indicator}
            key={`${id}${index}`}
            width={options.indentSize}
          />
        ))}
      </div>
    );
  }, [parent, rowIndex, level, id, options, isVisualSibling, data, subType]);

const IndentComponent = memo(({ indicator, width }) => (
  <div
    className={cx("d-flex flex-row h-100", s.spacerContainer)}
    style={{ width }}
  >
    <div className="w-50 h-100"></div>
    <div className="d-flex flex-column w-50 h-100">
      <div
        className={cx("h-50 w-100 border-gray-400", {
          "border-bottom":
            indicator === "last-sibling" || indicator === "sibling",
          "border-start": !(indicator === "none"),
        })}
      ></div>
      <div
        className={cx("h-50 w-100 border-gray-400", {
          "border-start": indicator === "sibling" || indicator === "ancestor",
        })}
      ></div>
    </div>
  </div>
));

const CompactIndentComponent = memo(({ indicator, width }) => (
  <div
    className={cx("d-flex flex-row h-100", s.spacerContainer)}
    style={{ width }}
  >
    <div className="d-flex flex-column w-100 h-100">
      <div
        className={cx("h-50 w-100 border-gray-400", {
          "border-bottom":
            indicator === "last-sibling" || indicator === "sibling",
          "border-start": !(indicator === "none"),
        })}
      ></div>
      <div
        className={cx("h-50 w-100 border-gray-400", {
          "border-start": indicator === "sibling" || indicator === "ancestor",
        })}
      ></div>
    </div>
  </div>
));

function determineSpacerType({
  isVisualSibling,
  subType,
  level,
  levelDiff,
  hasSiblingsAfter,
}) {
  // Handle all visual sibling cases first
  if (isVisualSibling) {
    if (subType === "roomcontrolwireless") {
      if (level === 1) {
        return hasSiblingsAfter ? "sibling" : "last-sibling";
      }
      if (level === 2 && levelDiff === 1) {
        return hasSiblingsAfter ? "sibling" : "last-sibling";
      }
      if (level >= 3 && levelDiff === 2) {
        return hasSiblingsAfter ? "sibling" : "last-sibling";
      }
    }

    // Universal visual sibling rules
    if (levelDiff === 1) {
      return "none";
    } // No line to immediate parent
    if (levelDiff === 2) {
      return hasSiblingsAfter ? "sibling" : "last-sibling";
    } // Grandparent gets sibling lines
    return hasSiblingsAfter ? "ancestor" : "none";
  }

  // Standard non-visual case
  if (levelDiff === 1) {
    return hasSiblingsAfter ? "sibling" : "last-sibling";
  }
  return hasSiblingsAfter ? "ancestor" : "none";
}

const getSpacers = ({ parent, rowIndex, level, isVisualSibling, subType }) => {
  if (parent.level < 0) {
    return [];
  }

  const spacerList = [];
  let currentParent = parent;

  let levelsToProcess = level;

  while (currentParent && currentParent.level >= 0 && levelsToProcess > 0) {
    const lastChildRowIndex = currentParent.children.at(-1).rowIndex;
    const levelDiff = level - currentParent.level;
    const hasSiblingsAfter = lastChildRowIndex > rowIndex;

    spacerList.push(
      determineSpacerType({
        isVisualSibling,
        subType,
        level,
        levelDiff,
        hasSiblingsAfter,
      }),
    );

    currentParent = currentParent.parent;
    levelsToProcess -= 1;
  }

  return spacerList.reverse();
};
